import styled from "@emotion/styled"
import { PText } from "@porsche-design-system/components-react"
import { radiusLg, spacingStaticSm } from "@porsche-design-system/components-react/emotion"
import { useController, useFormContext } from "react-hook-form"
import { useIntl } from "react-intl"
import { formMessages } from "src/common/form.messages"
import { shouldForwardProp } from "src/common/shouldForwardProp"
import { SmallIconOverlay } from "src/components/smallIconOverlay/SmallIconOverlay"
import { FieldWrapper } from "../../fieldWrapper/FieldWrapper"

export interface Option {
	hexCode: string
	label: string
	value: string
}

export interface ColorRadioButtonGroupProps {
	label: string
	name: string
	options: Option[]
	required?: boolean
}

export const ColorRadioGroupField = ({ label, name, options, required }: ColorRadioButtonGroupProps) => {
	const { control } = useFormContext()
	const { formatMessage } = useIntl()

	const {
		field,
		fieldState: { error },
	} = useController({
		control,
		name,
		rules: {
			required: required ? formatMessage(formMessages.required) : undefined,
		},
	})

	const { onChange: fieldOnChange, ref: fieldRef, value: fieldValue } = field

	return (
		<FieldWrapper label={label} required={required} error={error}>
			<Layout ref={fieldRef}>
				{options.map((option) => (
					<ColorRadioButton
						name={name}
						key={option.value}
						isSelected={fieldValue === option.value}
						onChange={fieldOnChange}
						{...option}
					/>
				))}
			</Layout>
		</FieldWrapper>
	)
}

interface ColorRadioButtonProps {
	hexCode: string
	isSelected: boolean
	label: string
	name: string
	onChange: (...event: any[]) => void
	value: string
}

const ColorRadioButton = ({ hexCode, isSelected, label, name, onChange, value }: ColorRadioButtonProps) => {
	return (
		<Label>
			<Input
				type="radio"
				id={value}
				name={name}
				onChange={(event) => event.target.value === "on" && onChange(value)}
			/>
			<SmallIconOverlay icon="check" visible={isSelected} backgroundHexCode={hexCode}>
				<ColorSquare $backgroundColor={hexCode} />
			</SmallIconOverlay>
			<PText>{label}</PText>
		</Label>
	)
}

const Layout = styled.div`
	display: grid;
	gap: 1rem;
	grid-template-columns: repeat(auto-fill, minmax(15em, 1fr));
`

const Label = styled.label`
	display: flex;
	align-items: center;
	gap: ${spacingStaticSm};
`

const Input = styled.input`
	display: none;
`

const ColorSquare = styled("div", { shouldForwardProp })<{ $backgroundColor: string }>`
	display: inline-block;
	height: 24px;
	width: 24px;
	box-sizing: border-box;
	vertical-align: middle;
	background: ${({ $backgroundColor }) => $backgroundColor};
	border: 1px solid black;
	border-radius: ${radiusLg};
`
